<template>
  <div class="partner-management-page">
    <hr class="separator " style="margin-top: 1px"/>

    <div>
      <div class="custom-text">
        <span class="user-manage">{{ $t('PartnerManagement.title') }}</span>
      </div>
      <hr class="separator"/>
    </div>

    <SearchBar @search="handleSearch"/>
    <div class="toolbar">
      <el-button class="button-background-color" @click="handleExport" :loading="exportLoading">
        {{ $t('PartnerManagement.export') }}
      </el-button>
    </div>
    <DataTable :data="tableData" :loading="loading" @refresh="loadData"/>
    <div class="pagination">
      <PaginationBar :totalRecords="totalRecords" :current-page="currentPage" @page-change="handlePageChange"
                     @page-size-change="handlePageSizeChange" :page-size="pageSize"/>
    </div>
  </div>
</template>

<script>
import SearchBar from './components/SearchBar.vue';
import DataTable from './components/DataTable.vue';
import PaginationBar from '@/components/PaginationBar.vue';
import { ElMessage } from 'element-plus';
import { getPartnerList, exportMemberApply } from '@/api/api';

export default {
  components: {
    SearchBar,
    DataTable,
    PaginationBar,
  },
  data() {
    return {
      tableData: [],
      totalRecords: 0,
      currentPage: 1,
      pageSize: 10,
      isSearching: false,
      searchParams: {},
      loading: false,
      exportLoading: false,
      userPermissions: [],
    };
  },
  
  computed: {},

  created() {
    this.loadData();
  },
  
  mounted() {
    this.loadUserPermissions();
  },
  watch: {
    '$i18n.locale': function () {
      if (this.isSearching) {
        this.handleSearch(this.searchParams);
      } else {
        this.loadData();
      }
    },
  },
  methods: {
    async loadUserPermissions() {
      try {
        const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
        const cacheKey = `userPermissions_${userId}`;
        const cache = localStorage.getItem(cacheKey) || sessionStorage.getItem(cacheKey);
        if (cache) {
          this.userPermissions = JSON.parse(cache);
        }
      } catch (error) {
        console.error('Error accessing storage:', error);
        this.userPermissions = [];
      }
    },
    
    hasPermission(permission) {
      if (!this.userPermissions || !Array.isArray(this.userPermissions)) {
        return false;
      }
      
      const hasPermissionRecursive = (permissions) => {
        return permissions.some(perm => {
          if (perm.code === permission) {
            return true;
          }
          if (perm.children && Array.isArray(perm.children)) {
            return hasPermissionRecursive(perm.children);
          }
          return false;
        });
      };
      
      return hasPermissionRecursive(this.userPermissions);
    },

    applyClientSideFilters(list, searchParams) {
      if (!Array.isArray(list)) return list;
      const result = list.filter(item => {
        // 国家筛选：匹配多个值之一
        const countryIn = searchParams['country-in'];
        if (Array.isArray(countryIn) && countryIn.length > 0) {
          const rowCountry = item.country || '';
          if (!countryIn.some(c => c === rowCountry)) {
            return false;
          }
        }

        // 联系人姓名模糊匹配
        const contactLike = searchParams['contact_name-like'];
        if (contactLike && typeof contactLike === 'string') {
          const name = (item.contactName || '').toLowerCase();
          if (!name.includes(contactLike.toLowerCase())) return false;
        }

        // 公司名称模糊匹配
        const storeLike = searchParams['store_name-like'];
        if (storeLike && typeof storeLike === 'string') {
          const store = (item.storeName || '').toLowerCase();
          if (!store.includes(storeLike.toLowerCase())) return false;
        }

        // 申请时间范围
        const begin = searchParams['apply_time-begin'];
        const end = searchParams['apply_time-end'];
        if (begin || end) {
          const applyTime = item.applyTime ? new Date(item.applyTime).getTime() : null;
          if (applyTime !== null && !Number.isNaN(applyTime)) {
            if (begin) {
              const beginTs = new Date(begin).getTime();
              if (!Number.isNaN(beginTs) && applyTime < beginTs) return false;
            }
            if (end) {
              const endTs = new Date(end).getTime();
              if (!Number.isNaN(endTs) && applyTime > endTs) return false;
            }
          }
        }

        return true;
      });
      return result;
    },

    // 把搜索参数扩展为多种形式，兼容 /api/users 对 partner_info 嵌套字段的筛选
    // 1) 顶层键 country-in=xxx, contact_name-like=xxx（数组用 [] 或逗号拼接两种格式）
    // 2) 嵌套 partner_info.country-in=xxx （后端若支持嵌套字段筛选）
    // 3) country_ids-in=xxx （用户模型可能支持的别名）
    buildSearchParams(searchParams) {
      const params = { ...searchParams };

      const countryIn = searchParams['country-in'];
      if (Array.isArray(countryIn) && countryIn.length > 0) {
        // 数组同时用两种常见后端格式传递
        params['partner_info.country-in'] = countryIn;
        params['country_ids-in'] = countryIn;
        // 同时保留原 key，让后端自行识别
      }

      const contactLike = searchParams['contact_name-like'];
      if (contactLike) {
        params['partner_info.contact_name-like'] = contactLike;
      }

      const storeLike = searchParams['store_name-like'];
      if (storeLike) {
        params['partner_info.store_name-like'] = storeLike;
      }

      return params;
    },
    
    async handleSearch(searchParams) {
      const pageSize = this.pageSize;

      this.isSearching = true;
      const baseParams = { ...searchParams, "is_partner-eq": 1 };
      this.searchParams = this.buildSearchParams(baseParams);
      this.currentPage = 1;
      this.loading = true;
      try {
        const response = await getPartnerList(1, pageSize, this.searchParams);
        if (response && response.data) {
          const mapped = response.data.map(item => this.mapResponseFields(item));
          // 客户端兜底过滤：对当前页数据再过滤一次，确保用户立刻看到筛选效果。
          // 仅作为展示层补充，totalRecords 仍取后端返回值以保证分页正确性。
          const filtered = this.applyClientSideFilters(mapped, this.searchParams);
          this.tableData = filtered;
          this.totalRecords = response.meta?.total || mapped.length;
          this.currentPage = response.meta?.current_page || 1;
          this.pageSize = response.meta?.per_page ? parseInt(response.meta.per_page) : pageSize;
        }
      } catch (error) {
        console.error(this.$t('ProductManage.searchFailed'), error);
      } finally {
        this.loading = false;
      }
    },

    async handlePageChange(newPage) {
      try {
        this.currentPage = newPage;
        if (this.isSearching) {
          await this.handleSearch(this.searchParams);
        } else {
          await this.loadData();
        }
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      }
    },

    async handlePageSizeChange(newPageSize) {
      try {
        this.pageSize = newPageSize;
        this.currentPage = 1;
        if (this.isSearching) {
          await this.handleSearch(this.searchParams);
        } else {
          await this.loadData();
        }
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      }
    },

    async loadData() {
      this.isSearching = false;
      const page = this.currentPage;
      const pageSize = this.pageSize;
      this.loading = true;
      try {
        const response = await getPartnerList(page, pageSize, { "is_partner-eq": 1 });
        if (response && response.data) {
          this.tableData = response.data.map(item => this.mapResponseFields(item));
          this.totalRecords = response.meta?.total || 0;
          this.currentPage = response.meta?.current_page || page;
          this.pageSize = response.meta?.per_page ? parseInt(response.meta.per_page) : pageSize;
        }
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'), error);
      } finally {
        this.loading = false;
      }
    },

    async handleExport() {
      this.exportLoading = true;
      try {
        const exportParams = this.isSearching ? this.searchParams : { "is_partner-eq": 1 };
        const exportedData = await exportMemberApply(exportParams);

        if (exportedData.code === -1) {
          ElMessage.error(exportedData.message || this.$t('PartnerManagement.exportFailed'));
          return;
        }
        const downloadLink = document.createElement('a');
        downloadLink.href = exportedData.url;
        downloadLink.download = this.$t('PartnerManagement.exportFileName');
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } catch (error) {
        console.error('Export failed:', error);
        ElMessage.error(this.$t('PartnerManagement.exportFailed'));
      } finally {
        this.exportLoading = false;
      }
    },

    getStatusText(status) {
      const numericStatus = Number(status);
      const statusMap = {
        0: this.$t('PartnerManagement.statusPending'),
        1: this.$t('PartnerManagement.statusApproved'),
        2: this.$t('PartnerManagement.statusRejected'),
        'pending': this.$t('PartnerManagement.statusPending'),
        'approved': this.$t('PartnerManagement.statusApproved'),
        'rejected': this.$t('PartnerManagement.statusRejected')
      };
      return statusMap[numericStatus] || statusMap[status] || status;
    },

    mapResponseFields(item) {
      const partnerInfo = item.partner_info || {};
      const auditStatus = partnerInfo.audit_status !== undefined ? partnerInfo.audit_status
        : (item.audit_status !== undefined ? item.audit_status
        : (item.applicantStatus || item.status));

      // 分隔字符串转数组
      const serviceCapabilityRaw = partnerInfo.service_capability || item.serviceCapabilities || item.serviceCapability || [];
      const serviceCapabilities = Array.isArray(serviceCapabilityRaw)
        ? serviceCapabilityRaw
        : (typeof serviceCapabilityRaw === 'string' ? serviceCapabilityRaw.split(',').filter(Boolean) : []);

      const cooperateReasonRaw = partnerInfo.cooperate_reason || item.howKnowAnanda || item.knowChannel || '';
      const howKnowAnanda = Array.isArray(cooperateReasonRaw)
        ? cooperateReasonRaw.join(',')
        : cooperateReasonRaw;

      return {
        ...item,
        id: item.id || partnerInfo.id,
        partner_info_id: partnerInfo.id || null,
        rate: item.rate !== undefined ? Number(item.rate) : (partnerInfo.rate !== undefined ? Number(partnerInfo.rate) : 0),
        dealer_rating: item.rate !== undefined ? Number(item.rate) : (partnerInfo.rate !== undefined ? Number(partnerInfo.rate) : 0),
        contactName: partnerInfo.contact_name || item.contactName || item.contact_name || '',
        storeName: partnerInfo.store_name || item.storeName || item.store_name || '',
        email: partnerInfo.email || item.email || '',
        tel: partnerInfo.tel || item.tel || item.phone || '',
        country: partnerInfo.country || item.country || '',
        postCode: partnerInfo.post_code || item.postCode || item.post_code || '',
        location: partnerInfo.location || item.location || '',
        taxNumber: partnerInfo.tax_number || item.taxNumber || item.tax_number || '',
        website: partnerInfo.website || item.website || '',
        businessLicense: partnerInfo.business_license || item.businessLicense || item.business_license || '',
        officeArea: partnerInfo.office_area || item.officeArea || item.office_area || '',
        warehouse: partnerInfo.warehouse_area || item.warehouse || item.warehouseArea || item.warehouse_area || '',
        staff: partnerInfo.staff_count || item.staff || item.staffCount || item.staff_count || '',
        serviceCapabilities: serviceCapabilities,
        serviceCapability: Array.isArray(serviceCapabilityRaw) ? serviceCapabilityRaw.join(',') : serviceCapabilityRaw,
        note: partnerInfo.note || item.note || '',
        remark: partnerInfo.note || item.note || item.remark || '',
        workWithAnanda: (partnerInfo.is_use_ananda !== undefined && partnerInfo.is_use_ananda !== null)
          ? (Number(partnerInfo.is_use_ananda) === 1 ? 'yes' : 'no')
          : (item.workWithAnanda || (item.isUseAnanda === 1 ? 'yes' : 'no')),
        is_use_ananda: (partnerInfo.is_use_ananda !== undefined && partnerInfo.is_use_ananda !== null)
          ? Number(partnerInfo.is_use_ananda)
          : (item.is_use_ananda !== undefined ? Number(item.is_use_ananda) : (item.isUseAnanda || 0)),
        howKnowAnanda: howKnowAnanda,
        whyChooseAnanda: partnerInfo.reason_extra || item.whyChooseAnanda || item.cooperateReason || item.cooperate_reason || '',
        additionalInfo: partnerInfo.reason_extra || item.additionalInfo || item.reasonExtra || item.reason_extra || '',
        subscribeNewsletter: (partnerInfo.is_subscribe !== undefined && partnerInfo.is_subscribe !== null)
          ? (Number(partnerInfo.is_subscribe) === 1)
          : (item.subscribeNewsletter || (item.isSubscribe === 1)),
        applicantStatus: auditStatus,
        statusText: this.getStatusText(auditStatus),
        applyTime: partnerInfo.apply_time || item.applyTime || item.apply_time || item.createdAt || item.created_at || '',
        reviewer: partnerInfo.audit_user_name || item.audit_user_name || item.reviewer || partnerInfo.audit_user_id || item.audit_user_id || item.auditor || '',
        audit_user_id: partnerInfo.audit_user_id || item.audit_user_id || null,
        reviewTime: partnerInfo.audit_time || item.reviewTime || item.auditTime || item.audit_time || '',
        reviewComment: partnerInfo.audit_comment || item.reviewComment || item.auditComment || item.audit_comment || ''
      };
    }
  },
};
</script>
<style scoped>
@import "@/assets/css/publicStyle.css";

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  margin-bottom: 20px;
}

.toolbar {
  margin: 15px 20px;
  display: flex;
  justify-content: flex-end;
}
</style>
